import { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Trash2, Save, ChevronDown, X } from 'lucide-react';
import { PublicRFQCriteria } from '@/lib/types/job';
import { usePublicRFQCriteria } from '@/hooks/usePublicRFQCriteria';
import { useCertificationTypes } from '@/hooks/useCertificationTypes';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { featureFlags, FEATURE_FLAGS } from '@/lib/featureFlags';

interface PublicRFQCriteriaBuilderProps {
  rfqId?: string; // Optional - won't exist during draft
  initialCriteria?: PublicRFQCriteria | Partial<PublicRFQCriteria> | null;
  onCriteriaChange?: (criteria: Partial<PublicRFQCriteria>) => void | Promise<void>;
  mode?: 'draft' | 'active'; // draft = before RFQ open, active = after RFQ open
  previewMatchCount?: number;
  previewTotalCount?: number;
}

export interface PublicRFQCriteriaBuilderRef {
  getCurrentCriteria: () => Partial<PublicRFQCriteria> | null;
}

export const PublicRFQCriteriaBuilder = forwardRef<PublicRFQCriteriaBuilderRef, PublicRFQCriteriaBuilderProps>(({
  rfqId, 
  initialCriteria, 
  onCriteriaChange,
  mode = 'active',
  previewMatchCount,
  previewTotalCount
}, ref) => {
  const { types: certificationTypes } = useCertificationTypes();
  const { perfectMatchCount, calculatePerfectMatches, saveCriteria, clearCriteria, loading } = usePublicRFQCriteria(rfqId);

  const [minRating, setMinRating] = useState<string>(initialCriteria?.min_rating?.toString() || '');
  const [minJobs, setMinJobs] = useState<string>(initialCriteria?.min_completed_jobs?.toString() || '');
  const [minVerification, setMinVerification] = useState<string | undefined>(initialCriteria?.min_verification_level || undefined);
  const [selectedCerts, setSelectedCerts] = useState<string[]>(initialCriteria?.required_certification_type_ids || []);
  const [calculating, setCalculating] = useState(false);
  const [enhancedEnabled, setEnhancedEnabled] = useState(true);
  const [premiumEnabled, setPremiumEnabled] = useState(true);

  // Check feature flags for verification levels
  useEffect(() => {
    const checkFlags = async () => {
      const [enhanced, premium] = await Promise.all([
        featureFlags.isEnabled(FEATURE_FLAGS.ENHANCED_VERIFICATION),
        featureFlags.isEnabled(FEATURE_FLAGS.PREMIUM_VERIFICATION)
      ]);
      setEnhancedEnabled(enhanced);
      setPremiumEnabled(premium);
    };
    checkFlags();
  }, []);

  // Expose method to get current criteria via ref
  useImperativeHandle(ref, () => ({
    getCurrentCriteria: () => {
      const hasCriteria = 
        (minRating && minRating !== '') || 
        (minJobs && minJobs !== '') || 
        minVerification || 
        selectedCerts.length > 0;
      
      if (!hasCriteria) return null;
      
      return {
        min_rating: minRating ? parseFloat(minRating) : null,
        min_completed_jobs: minJobs ? parseInt(minJobs) : null,
        min_verification_level: minVerification && minVerification !== '' ? minVerification as 'basic' | 'enhanced' | 'premium' : null,
        required_certification_type_ids: selectedCerts.length > 0 ? selectedCerts : null,
      };
    }
  }), [minRating, minJobs, minVerification, selectedCerts]);

  useEffect(() => {
    if (initialCriteria) {
      setMinRating(initialCriteria.min_rating?.toString() || '');
      setMinJobs(initialCriteria.min_completed_jobs?.toString() || '');
      setMinVerification(initialCriteria.min_verification_level || undefined);
      setSelectedCerts(initialCriteria.required_certification_type_ids || []);
    }
  }, [initialCriteria]);

  // Notify parent with debouncing to prevent excessive re-renders
  useEffect(() => {
    if (mode !== 'draft') return;

    const timer = setTimeout(() => {
      const criteriaData: Partial<PublicRFQCriteria> = {
        min_rating: minRating ? parseFloat(minRating) : null,
        min_completed_jobs: minJobs ? parseInt(minJobs) : null,
        min_verification_level: minVerification && minVerification !== '' ? minVerification as 'basic' | 'enhanced' | 'premium' : null,
        required_certification_type_ids: selectedCerts.length > 0 ? selectedCerts : null,
      };

      onCriteriaChange?.(criteriaData);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [minRating, minJobs, minVerification, selectedCerts, mode, onCriteriaChange]);

  const handleCalculate = useCallback(async () => {
    const criteriaData: Partial<PublicRFQCriteria> = {
      min_rating: minRating ? parseFloat(minRating) : null,
      min_completed_jobs: minJobs ? parseInt(minJobs) : null,
      min_verification_level: minVerification && minVerification !== '' ? minVerification as 'basic' | 'enhanced' | 'premium' : null,
      required_certification_type_ids: selectedCerts.length > 0 ? selectedCerts : null,
    };

    setCalculating(true);
    
    if (mode === 'active' && rfqId) {
      // In active mode, save to DB
      await calculatePerfectMatches({ ...criteriaData, rfq_id: rfqId });
    }
    
    setCalculating(false);
  }, [minRating, minJobs, minVerification, selectedCerts, mode, rfqId, calculatePerfectMatches]);

  // Stable key for selectedCerts to prevent unnecessary effect triggers
  const selectedCertsKey = useMemo(() => selectedCerts.join(','), [selectedCerts]);

  // Auto-calculate match count preview with debouncing (only for API calls)
  useEffect(() => {
    const hasCriteria = 
      (minRating && minRating !== '') || 
      (minJobs && minJobs !== '') || 
      minVerification || 
      selectedCerts.length > 0;
    
    if (!hasCriteria) {
      return;
    }

    const timer = setTimeout(() => {
      handleCalculate();
    }, 800); // Debounce by 800ms

    return () => {
      clearTimeout(timer);
    };
  }, [minRating, minJobs, minVerification, selectedCertsKey, handleCalculate]);

  const handleSave = async () => {
    const criteriaData: Partial<PublicRFQCriteria> = {
      rfq_id: rfqId,
    };

    if (minRating) criteriaData.min_rating = parseFloat(minRating);
    if (minJobs) criteriaData.min_completed_jobs = parseInt(minJobs);
    if (minVerification) criteriaData.min_verification_level = minVerification as any;
    if (selectedCerts.length > 0) criteriaData.required_certification_type_ids = selectedCerts;

    await saveCriteria(criteriaData);
  };

  const handleClear = async () => {
    await clearCriteria();
    setMinRating('');
    setMinJobs('');
    setMinVerification(undefined);
    setSelectedCerts([]);
  };

  const hasCriteria = 
    (minRating && minRating !== '') || 
    (minJobs && minJobs !== '') || 
    minVerification || 
    selectedCerts.length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Vendor Match Criteria
          {mode === 'draft' ? (
            <Badge variant="secondary">Preview Mode</Badge>
          ) : (
            <Badge variant="secondary">Optional</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Set criteria to calculate match scores for vendor bids. All verified vendors can still bid regardless of match score.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-6">
          {/* 2x2 grid for criteria fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="minRating">Minimum Rating</Label>
              <Input
                id="minRating"
                type="number"
                min="0"
                max="5"
                step="0.1"
                placeholder="e.g., 4.0"
                value={minRating}
                onChange={(e) => setMinRating(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="minJobs">Minimum Completed Jobs</Label>
              <Input
                id="minJobs"
                type="number"
                min="0"
                placeholder="e.g., 10"
                value={minJobs}
                onChange={(e) => setMinJobs(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="minVerification">Minimum Verification Level</Label>
              <Select value={minVerification ?? ""} onValueChange={(value) => setMinVerification(value || undefined)}>
                <SelectTrigger id="minVerification">
                  <SelectValue placeholder="No requirement (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  {enhancedEnabled && (
                    <SelectItem value="enhanced">Enhanced</SelectItem>
                  )}
                  {premiumEnabled && (
                    <SelectItem value="premium">Premium</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Required Certifications - Dropdown */}
            <div className="space-y-2">
              <Label>Required Certifications</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between"
                    type="button"
                  >
                    {selectedCerts.length > 0 ? (
                      <span className="truncate">
                        {selectedCerts.length} certification{selectedCerts.length !== 1 ? 's' : ''} selected
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Select certifications...</span>
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0 bg-popover z-50" align="start">
                  <ScrollArea className="h-[300px]">
                    <div className="p-4 space-y-2">
                      {certificationTypes.map((cert) => (
                        <div key={cert.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`cert-${cert.id}`}
                            checked={selectedCerts.includes(cert.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedCerts([...selectedCerts, cert.id]);
                              } else {
                                setSelectedCerts(selectedCerts.filter(id => id !== cert.id));
                              }
                            }}
                          />
                          <Label htmlFor={`cert-${cert.id}`} className="text-sm font-normal cursor-pointer flex-1">
                            {cert.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Selected Certifications Display */}
          {selectedCerts.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedCerts.map((certId) => {
                const cert = certificationTypes.find(c => c.id === certId);
                return cert ? (
                  <Badge key={certId} variant="secondary" className="flex items-center gap-1">
                    {cert.name}
                    <X
                      className="h-3 w-3 cursor-pointer hover:text-destructive"
                      onClick={() => setSelectedCerts(selectedCerts.filter(id => id !== certId))}
                    />
                  </Badge>
                ) : null;
              })}
            </div>
          )}
        </div>

        {hasCriteria && (
          <div className="border-2 border-primary/50 bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg p-4 shadow-md">
            <div className="flex items-baseline gap-3">
              <p className="text-base font-semibold text-foreground">Ideal Match Vendors:</p>
              <p className="text-sm text-muted-foreground">
                {(() => {
                  const displayCount = mode === 'draft' ? previewMatchCount : perfectMatchCount;
                  const displayTotal = mode === 'draft' ? previewTotalCount : undefined;
                  
                  if (calculating) {
                    return <span className="text-muted-foreground">Calculating...</span>;
                  }
                  
                  if (displayCount !== undefined && displayCount !== null) {
                    if (displayTotal !== undefined && displayTotal !== null) {
                      return (
                        <>
                          <span className="text-2xl font-bold text-primary">{displayCount}</span> <span className="font-medium">vendor{displayCount !== 1 ? 's' : ''} score{displayCount === 1 ? 's' : ''} 90%+ match</span> out of <span className="text-xl font-bold text-foreground">{displayTotal}</span> total vendor{displayTotal !== 1 ? 's' : ''}
                        </>
                      );
                    }
                    return (
                      <>
                        <span className="text-2xl font-bold text-primary">{displayCount}</span> <span className="font-medium">vendor{displayCount !== 1 ? 's' : ''} score{displayCount === 1 ? 's' : ''} 90%+ match</span>
                      </>
                    );
                  }
                  return <span className="text-muted-foreground">Calculating...</span>;
                })()}
              </p>
            </div>
          </div>
        )}

          {mode === 'active' && (
            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={loading || !hasCriteria}>
                <Save className="h-4 w-4 mr-2" />
                Save Criteria
              </Button>
              {initialCriteria && (
                <Button variant="outline" onClick={handleClear} disabled={loading}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear Criteria
                </Button>
              )}
            </div>
          )}
      </CardContent>
    </Card>
  );
});
